import { Record, Set, Map } from 'immutable';
import shortid from 'shortid';
import { format, formatDistance } from 'date-fns';
import { displayNamesMap } from '@saucelabs/sl-util-helpers';

import {
  SESSION_STATUS_CONFIGURING,
  SESSION_STATUS_IN_PROGRESS,
  SESSION_STATUS_ERROR,
  SESSION_STATUSES_LOADING,
  SESSION_STATUS_WAITING,
  SESSION_STATUSES_COMPLETED,
  APP_UPLOAD_STATUS,
  LAST_CHANGE_TIME_FORMAT,
} from './constants';

const APP_STORAGE_LIFETIME = 1000 * 60 * 60 * 24 * 60;

const SessionRecordClass = Record({
  error: false,
  id: undefined,
  job_id: null,
  members: Set(),
  owner: null,
  label: '',
  manual: false,
  metadata: Map({
    browser: '',
    browserDisplay: '',
    browserVersionDisplay: '',
    os: '',
    osDisplay: '',
    osVersionDisplay: '',
    resolution: '',
    version: '',
    owner: '',
    url: '',
  }),
  public: '',
  stacked: false,
  status: SESSION_STATUS_CONFIGURING,
  task_id: null,
  video_secret: null,
  _autoLaunch: false,
  _formData: {},
});

class SessionRecord extends SessionRecordClass {
  constructor(data = {}) {
    super({ id: data.id || shortid.generate(), ...data });
  }

  /* STATES */
  hasRunningStatus() {
    return this.get('status') === SESSION_STATUS_IN_PROGRESS;
  }

  isForm() {
    return this.get('status') === SESSION_STATUS_CONFIGURING;
  }

  isWaiting() {
    return this.get('status') === SESSION_STATUS_WAITING;
  }

  isLoading() {
    return SESSION_STATUSES_LOADING.includes(this.get('status')) && (
      !this.get('video_secret') || !this.get('task_id') || !this.get('job_id')
    );
  }

  isRunning() {
    return this.hasRunningStatus() && this.get('video_secret') && this.get('task_id') && this.get('job_id');
  }

  isCompleted() {
    return SESSION_STATUSES_COMPLETED.includes(this.get('status'));
  }

  hasError() {
    return this.get('status') === SESSION_STATUS_ERROR || this.get('error');
  }

  /* Helpers & getter */
  getSessionPath() {
    return `${document.location.origin}/live/${this.get('task_id')}`;
  }

  getStrippedUrl() {
    const url = this.getIn(['metadata', 'url']);
    if (!url) {
      return null;
    }

    const noProtocol = url.split('//')[1];
    const noWWW = noProtocol.replace(/^www./, '');
    return noWWW;
  }

  hasVisitors() {
    // By default session owner is also present in members list
    return this.get('members').size > 1;
  }

  visitorsCount() {
    // By default session owner is also present in members list
    return this.get('members').size - 1;
  }
}

const LogLineRecordClass = Record({
  key: null,
  level: null,
  message: null,
  time: null,
  timestamp: null,
});

class LogLineRecord extends LogLineRecordClass { }

const AppStorageRecordClass = Record({
  id: null,
  kind: null,
  access: null,
  name: null,
  owner: null,
  etag: null,
  upload_timestamp: null,
  metadata: null,
  group_id: null,
});

class AppStorageRecord extends AppStorageRecordClass {
  getCreationDate() {
    return new Date(this.upload_timestamp * 1000);
  }

  getCreationDateLabel() {
    return format(this.getCreationDate(), LAST_CHANGE_TIME_FORMAT);
  }

  getCreationDatePrettyLabel() {
    return `${formatDistance(this.getCreationDate(), new Date())} ago`;
  }

  getDeletionDate() {
    return new Date(this.upload_timestamp * 1000 + APP_STORAGE_LIFETIME);
  }

  getDaysUntilDeletion() {
    return (this.getDeletionDate().getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  }

  getDeletionDatePrettyLabel() {
    const daysLeft = Math.floor(this.getDaysUntilDeletion());
    if (daysLeft < 1) {
      return 'Expires today';
    }
    return `Expires in ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`;
  }

  getIconUrl() {
    return `data:image/png;base64,${this.metadata?.icon}`;
  }
}

const AppStorageGroupRecordClass = Record({
  id: null,
  name: null,
  recent: null,
  count: null,
  access: null,
  settings: null,
});

class AppStorageGroupRecord extends AppStorageGroupRecordClass {
  constructor(group) {
    super({
      ...group,
      recent: new AppStorageRecord(group.recent),
    });
  }

  getCreationDate() {
    return this.recent.getCreationDate();
  }

  getCreationDateLabel() {
    return this.recent.getCreationDateLabel();
  }

  getCreationDatePrettyLabel() {
    return this.recent.getCreationDatePrettyLabel();
  }

  getDeletionDate() {
    return this.recent.getDeletionDate();
  }

  getDaysUntilDeletion() {
    return this.recent.getDaysUntilDeletion();
  }

  getDeletionDatePrettyLabel() {
    return this.recent.getDeletionDatePrettyLabel();
  }

  getAdditionalVersionCount() {
    return parseInt(this.count, 10) - 1;
  }

  getIconUrl() {
    return `data:image/png;base64,${this.recent.metadata?.icon}`;
  }
}

const AppUploadingRecordClass = Record({
  id: null,
  displayName: null,
  error: null,
  status: APP_UPLOAD_STATUS.UPLOADING,
  progress: 0,
});

class AppUploadingRecord extends AppUploadingRecordClass {
  getPrettyProgress() {
    return `${this.progress * 100}%`;
  }
}

const AppVersionRecordClass = Record({
  creationTime: null,
  id: null,
  name: null,
  nameFromFile: null,
  packageName: null,
  platform: null,
  version: null,
  versionCode: null,
});

class AppVersionRecord extends AppVersionRecordClass {
  getFullSpecifications() {
    return {
      Version: this.version,
      'Version Code': this.versionCode,
      'Your Version Name': this.name,
      Platform: this.getPrettyPlatform(),
      ID: this.id,
      Uploaded: this.getPrettyCreationTime(),
    };
  }

  getPrettyCreationTime() {
    return `${formatDistance(new Date(this.creationTime), new Date())} ago`;
  }

  getPrettyPlatform() {
    return displayNamesMap[this.platform.toLowerCase()];
  }
}

export {
  AppStorageRecord,
  AppStorageGroupRecord,
  AppUploadingRecord,
  AppVersionRecord,
  LogLineRecord,
  SessionRecord,
  SessionRecordClass,
};
